import { lazy, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { ModelType } from '../../../types';
import { fetchWithType } from '../../../utils/api';
import {
  getAdminUrl,
  getApiToken,
  getUniqId,
  locationIsKia,
} from '../../../utils/helpers';
import { AppProps } from '../../App';
import CarouselCard from '../../components/carousel/CarouselCard';
import CarouselFilters from '../../components/carousel/CarouselFilters';
import { getStoreByKey } from '../../components/carousel/store/CarouselStore';
import { CarouselFieldsEnum } from '../../components/carousel/types/CarouselTypes';
import Modal from '../../components/ui/Modal';
import PrimaryButton from '../../components/ui/PrimaryButton';

const Snippet = lazy(() => import('../../components/Snippet'));
const CarouselControls = lazy(
  () => import('../../components/carousel/CarouselControls'),
);

const maybeUniqId = getUniqId();

export interface ModelAndTrimcode {
  model: ModelType;
  trimCode: string;
}

const PriceCardGrid = ({
  fields,
  imageType,
  trimCodes,
  modelCodes,
  hideControls,
  uniqKey,
  onChangeTrims,
}: {
  hideControls?: boolean;
  uniqKey?: string;
  onChangeTrims?: (t: ModelAndTrimcode[]) => void;
} & Pick<AppProps, 'fields' | 'imageType' | 'trimCodes' | 'modelCodes'>) => {
  const uniqId = uniqKey || maybeUniqId;
  const { dispatch, options, direction } = useStore(getStoreByKey(uniqId));

  const [cars, setCars] = useState<ModelAndTrimcode[]>([]);
  const [models, setModels] = useState<ModelType[]>([]);
  const [addTrim, setAddTrim] = useState(false);
  const [selectModel, setSelectModel] = useState<ModelType | null>(null);
  const [selectedTrimCode, setSelectedTrimCode] = useState<string | null>(null);

  useEffect(() => {
    if (onChangeTrims) {
      onChangeTrims(cars);
    }
  }, [cars]);

  const setData = (models: ModelType[]) => {
    if (imageType) {
      dispatch({ type: 'IMAGE_TYPE', payload: imageType });
    }
    if (fields) {
      dispatch({
        type: 'CONTROLS',
        payload: fields.split(',') as CarouselFieldsEnum[],
      });
    }
    dispatch({ type: 'IS_LOADED', payload: true });

    // set the models and trim codes array
    if (modelCodes && trimCodes) {
      setCars(
        modelCodes.split(',').map((modelCode, i) => ({
          model: models.find((m) => m.code === modelCode),
          trimCode: trimCodes.split(',')[i],
        })),
      );
    }
  };

  const fetchModels = async () => {
    let models =
      JSON.parse(sessionStorage.getItem('kia-carousel-models')) ?? [];
    if (!models.length) {
      try {
        const { data } = await fetchWithType<ModelType[]>(
          getAdminUrl() + '/api/models',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${getApiToken()}`,
            },
          },
        );
        if (data && data.length) {
          models = data;
          setModels(data);
        }
      } catch (error) {
        console.error('Error fetching trims:', error);
      }
    } else {
      setModels(models);
    }
    setData(models);
  };

  useEffect(() => {
    dispatch({ type: 'DIRECTION', payload: 'vertical' });
    dispatch({ type: 'SHOW_PRICELIST', payload: false });
    fetchModels();
  }, []);

  const getSnippetProps: () => string = () => {
    return `modelCodes="${cars.map((c) => c.model.code).join(',')}" trimCodes="${cars.map((c) => c.trimCode).join(',')}" ${
      !options
        ? 'pricelist="true"'
        : Object.entries(options)
            .map(([key, value]) => (value ? `${key}="${value}"` : ''))
            .filter(Boolean)
            .join(' ')
    }`;
  };

  const gridCols = () => {
    switch (cars.length) {
      case 2:
        return 'sm:grid-cols-2';
      case 3:
        return 'sm:grid-cols-3';
      default:
        return cars.length > 3 ? 'sm:grid-cols-4' : '';
    }
  };

  return (
    <div
      className={`pt-4 lg:px-4 pb-12 ${direction === 'vertical' ? 'max-w-screen-kia mx-auto' : ''}`}
    >
      {!locationIsKia() && !hideControls ? (
        <>
          {addTrim ? (
            <Modal clickOutside={() => setAddTrim(false)}>
              <div className="bg-white p-8 grid gap-4">
                <h2>Välj modell</h2>
                <select
                  className="w-full p-2 border border-black"
                  onChange={(e) => {
                    const model = models.find((m) => m.code === e.target.value);
                    setSelectModel(model);
                    setSelectedTrimCode(model.first_car.code);
                  }}
                >
                  <option>Välj modell</option>
                  {models.map((model, i) => (
                    <option key={i} value={model.code}>
                      {model.name}
                    </option>
                  ))}
                </select>
                {selectModel ? (
                  <div>
                    <h3>{selectModel.name}</h3>
                    <select
                      onChange={(e) => {
                        setSelectedTrimCode(e.target.value);
                      }}
                      className="w-full p-2 border border-black"
                    >
                      {selectModel.visible_cars.map((t, i) => (
                        <option key={i} value={t.code}>
                          {t.level
                            ? `${selectModel.name} | ${t.level.name}`
                            : t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <PrimaryButton
                  onClick={() => {
                    setCars((prev) => [
                      ...prev,
                      { model: selectModel, trimCode: selectedTrimCode },
                    ]);
                    setSelectModel(null);
                    setSelectedTrimCode(null);
                    setAddTrim(false);
                  }}
                >
                  Lägg till trim
                </PrimaryButton>
              </div>
            </Modal>
          ) : null}
          {models?.length ? (
            <div className="max-w-screen-kia-sm m-auto flex flex-col justify-center items-center">
              <h2 className="text-center mb-2">Valda trims</h2>
              {cars.map((car, i) => (
                <div key={i} className="flex gap-2 justify-center items-center">
                  <p>{car.model?.name}:</p>
                  <p>{car.trimCode}</p>
                  <button
                    className="text-red-500"
                    onClick={() => {
                      setCars((prevCars) =>
                        prevCars.filter((c) => c.trimCode !== car.trimCode),
                      );
                    }}
                  >
                    X
                  </button>
                </div>
              ))}
              <div className="max-w-40 mt-2">
                <PrimaryButton onClick={() => setAddTrim(true)}>
                  Lägg till en trim
                </PrimaryButton>
              </div>
            </div>
          ) : null}
          <CarouselControls uniqId={uniqId} type="grid" />
          <Snippet page="cardgrid" props={getSnippetProps()} />
        </>
      ) : null}
      <div className="max-w-screen-kia mx-auto relative">
        {!options ? <CarouselFilters uniqId={uniqId} single /> : null}
      </div>
      <div className={`px-4 lg:px-0 grid mx-auto gap-2 ${gridCols()}`}>
        {cars.map((car) => (
          <CarouselCard
            uniqId={uniqId}
            type="grid"
            key={car.trimCode}
            model={car.model}
            trimCode={car.trimCode}
          />
        ))}
      </div>
    </div>
  );
};

export default PriceCardGrid;
